import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import MoreIcon from '@mui/icons-material/MoreHoriz';
import CableIcon from '@mui/icons-material/Cable';
import Button from 'components/Button';
import Spin from 'components/Spin';
import TextInput from 'components/form/TextInput';
import Select from 'components/form/Select';
import IconButton from 'components/IconButton';
import { useTranslation } from 'react-i18next';
import {
  getConnectPoints,
  saveWebsiteLink,
  getWebsiteLinks,
  deleteWebsiteLink,
  scanWebsiteUrls,
  verifyIntegration,
} from 'services/connect-card.service';
import toast, { snack } from 'utils/toast';
import { isUrl } from 'utils';
import Dropdown from 'components/Dropdown';

const VerifyWebsite = ({ onSuccess }) => {
  const [url, setUrl] = useState('');
  const [hasError, setHasError] = useState(false);

  const onVerify = async () => {
    if (!isUrl(url)) {
      return snack.error('Please enter a valid URL');
    }
    const verifyResp = await verifyIntegration(url);
    // TODO change
    if (verifyResp.verified) {
      const { pages } = await scanWebsiteUrls(url);
      onSuccess(pages);
    } else {
      setHasError(true);
      toast.error(
        verifyResp.message ||
          'Unable to verify website. Please verify you have installed the script correctly.',
      );
    }
  };

  const onSkip = () => {
    onSuccess([]);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography paragraph fontWeight={500}>
        Verify you’re linked successfully
      </Typography>
      <Typography paragraph color="text.secondary">
        We will run a scan on the URL provided to confirm that your website was
        linked successfully.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="end">
        <TextInput
          fullWidth
          margin="none"
          size="small"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          label="Paste your URL"
          placeholder="https://www.citychurch.com"
        />
        <Button disabled={!url} onClick={onVerify}>
          Verify
        </Button>
        {hasError && (
          <Button variant="outlined" disabled={!url} onClick={onSkip}>
            Skip
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

const LinkItem = ({ link, onChange, onDelete, widgets }) => {
  useEffect(() => {
    if (link.hash && link.is_verified === '0') {
      verifyLink();
    }
  }, []);

  const onUrlChange = (e) => {
    link.label = e.target.value;
    onChange(link);
  };

  const verifyLink = () => {
    return verifyIntegration(link.label).then((resp) => {
      return saveWebsiteLink({
        hash: link.hash,
        is_verified: resp.verified ? '1' : '0',
      }).then((result) => {
        link.is_verified = result.is_verified;
        onChange(link);
        return resp;
      });
    });
  };

  const onUrlBlur = async () => {
    if (link.label && link.value && link.hash) {
      await saveWebsiteLink({
        website_url: link.label,
        hash: link.hash,
      });
    }
  };

  const handleMenu = async (key) => {
    if (key === 'delete') {
      await deleteWebsiteLink(link.hash);
      onDelete(link);
    } else if (key === 'verify') {
      verifyLink().then((res) => {
        if (res.message) {
          snack.error(res.message);
        }
      });
    }
  };

  const onCardSelect = async (val) => {
    if (!isUrl(link.label)) return;
    await saveWebsiteLink({
      connect_card_collection_id: val,
      website_url: link.label,
      is_verified: '0',
      hash: link?.hash,
    })
      .then((r) => {
        link.value = val;
        link.hash = r.hash;
        link.is_verified = r.is_verified;
        onChange(link);
      })
      .catch((e) => snack.error(e.message));
  };

  return (
    <Grid container spacing={1} rowSpacing={3} alignItems="center">
      <Grid item md={6}>
        <TextInput
          fullWidth
          margin="none"
          value={link.label}
          onChange={onUrlChange}
          onBlur={onUrlBlur}
        />
      </Grid>
      <Grid item>
        <CableIcon
          color={link.is_verified === '1' ? 'success' : 'error'}
          fontSize="small"
          sx={{ transform: 'rotate(90deg)' }}
        />
      </Grid>
      <Grid item md={4}>
        <Select
          fullWidth
          transformValue
          onChange={onCardSelect}
          margin="none"
          options={widgets}
          value={link.value}
        />
      </Grid>
      <Grid item md={1}>
        <Dropdown
          onSelect={handleMenu}
          options={[
            { key: 'delete', children: 'Delete' },
            { key: 'verify', children: 'Re-Verify' },
          ]}
        >
          <IconButton edge="end" icon={<MoreIcon />} />
        </Dropdown>
      </Grid>
    </Grid>
  );
};

const LinkSetup = ({}) => {
  const [t] = useTranslation('groups');
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [links, setLinks] = useState();
  const account = useSelector((s) => s.settings.account);

  useEffect(() => {
    getConnectPoints({
      collection_type: 'widget',
    }).then((res) => {
      setWidgets(res.map((w) => ({ label: w.collection_name, value: w.id })));
    });
    getWebsiteLinks({
      account_hash: account.hash,
    })
      .then((res) => {
        if (res.length) {
          setLinks(
            res.map((i) => ({
              label: i.website_url,
              value: i.connect_card_collection_id,
              hash: i.hash,
              is_verified: i.is_verified,
            })),
          );
        }
      })
      .then(() => setLoading(false));
  }, []);

  const onCardSelect = (url) => async (val) => {
    if (!val) {
      const link = links.find((l) => l.label === '*');
      if (link) {
        try {
          await deleteWebsiteLink(link.hash);
          setLinks(links.filter((i) => i.hash !== link.hash));
        } catch (e) {
          snack.error(e.message);
        }
      }
    } else {
      const link = links.find((l) => l.label === url);
      await saveWebsiteLink({
        connect_card_collection_id: val,
        website_url: url,
        is_verified: '1',
        hash: link?.hash,
      })
        .then((r) => {
          if (link) {
            link.value = val;
            link.hash = r.hash;
            link.is_verified = r.is_verified;
            setLinks([...links]);
          } else {
            setLinks([...links, { label: url, value: val, hash: r.hash }]);
          }
        })
        .catch((e) => snack.error(e.message));
    }
  };

  const onLinkChange = (item) => {
    setLinks(links.map((i) => (i === item ? item : i)));
  };

  const onScrapedLinks = (links) => {
    const allLinks = links.splice(1);
    setLinks(allLinks.map((i) => ({ label: i, value: '' })));
  };

  const onDelete = (item) => {
    setLinks(links.filter((i) => i.hash !== item.hash));
  };

  const onAddPage = () => {
    setLinks([...links, { label: '', value: '' }]);
  };

  if (loading) {
    return <Spin spinning />;
  }
  if (!links) {
    return <VerifyWebsite onSuccess={onScrapedLinks} />;
  }

  const errorCount = links.filter((i) => i.is_verified === '0').length;
  const successCount = links.filter(
    (i) => i.is_verified === '1' && i.website_url !== '*',
  ).length;

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography gutterBottom fontWeight={500}>
          Verify you’re linked successfully
        </Typography>
        <Typography paragraph color="text.secondary">
          We scan the your churches website to confirm that your Connect Widget
          was embedded correctly.
        </Typography>
        <Box mb={4}>
          <Typography color="success.main" variant="body2" fontWeight={500}>
            • Code detected successfully! on {successCount} pages
          </Typography>
          {errorCount > 0 && (
            <Typography color="error.main" variant="body2" fontWeight={500}>
              • Error found on {errorCount} pages
            </Typography>
          )}
        </Box>
        <Grid container alignItems="center" spacing={2}>
          <Grid item md={6}>
            <Typography fontWeight={500}>Set Default Widget</Typography>
          </Grid>
          <Grid item md={6}>
            <Select
              fullWidth
              transformValue
              isClearable
              margin="none"
              options={widgets}
              value={links.find((i) => i.label === '*')?.value}
              onChange={onCardSelect('*')}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Box>
          {links
            .filter((i) => i.label !== '*')
            .map((i, idx) => (
              <LinkItem
                key={idx}
                link={i}
                onChange={onLinkChange}
                onDelete={onDelete}
                widgets={widgets}
              />
            ))}
        </Box>
        <Box mt={2}>
          <Button variant="outlined" onClick={onAddPage}>
            Add Page
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default LinkSetup;
